import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../config/URL";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import GlobalDelete from "../../components/common/GlobalDelete";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "STAFF";

const Staff = ({ selectedCenter }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    teacherName: "",
    country: "",
    teacherType: "",
    status: "",
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  /* -------------------- ROLE MATRIX -------------------- */
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const canIndex = Boolean(storedScreens?.staffIndex);
  const canRead = Boolean(storedScreens?.staffRead);
  const canCreate = Boolean(storedScreens?.staffCreate);
  const canUpdate = Boolean(storedScreens?.staffUpdate);
  const canDelete = Boolean(storedScreens?.staffDelete);

  const showActionColumn = canUpdate || canDelete;

  const defaultColumnVisibility = {
    citizenship: false,
    dateOfBirth: false,
    idNo: false,
    idType: false,
    createdBy: false,
    createdAt: false,
    updatedAt: false,
    updatedBy: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );
  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "role",
        enableHiding: true,
        header: "Role",
        Cell: ({ row }) =>
          row.original.role === "staff" ||
          row.original.role === "Staff" ||
          row.original.role === "STAFF" ? (
            <span className="badge badges-Green fw-light">Staff</span>
          ) : row.original.role === "center_manager" ? (
            <span className="badge badges-orange fw-light">Centre Manager</span>
          ) : row.original.role === "staff_admin" ? (
            <span className="badge badges-red fw-light">Staff Admin</span>
          ) : row.original.role === "marketing" ? (
            <span className="badge badges-Yellow fw-light">Marketing</span>
          ) : row.original.role === "branch_admin" ? (
            <span className="badge badges-Blue fw-light">Branch Admin</span>
          ) : null,
      },
      { accessorKey: "teacherName", header: "Staff Name" },
      { accessorKey: "userUniqueId", header: "Staff Id" },
      { accessorKey: "countryName", header: "Country" },
      { accessorKey: "teacherType", header: "Staff Type" },
      { accessorKey: "email", header: "Email" },
      { accessorKey: "gender", header: "Gender" },
      { accessorKey: "contactNumber", header: "Mobile" },
      { accessorKey: "citizenship", header: "Citizenship" },
      { accessorKey: "dateOfBirth", header: "Date Of Birth" },
      { accessorKey: "idNo", header: "Id No" },
      { accessorKey: "idType", header: "Id Type" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    return [
      baseColumns[0], // S.NO
      {
        accessorKey: "id",
        header: "",
        size: 20,
        enableSorting: false,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  /* -------------------- FETCH DATA -------------------- */
  const fetchData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      filters.centerId = selectedCenter;
      for (let key in filters) {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      }
      const baseUrl =
        selectedCenter === 0 || selectedCenter === "0"
          ? "getAllUserListExceptTeacher"
          : "getAllUserListExceptTeacher";
      queryParams.centerId = selectedCenter;
      const url = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;

      const response = await api.get(url);
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  useEffect(() => {
    fetchData();
  }, [filters, selectedCenter]);

  /* -------------------- FILTER HANDLERS -------------------- */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const clearFilter = () => {
    setFilters({
      centerId: selectedCenter,
      teacherName: "",
      country: "",
      teacherType: "",
      status: "",
    });
  };

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() => exportVisibleColumnsToCSV(table, data, "StaffList.csv")}
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: canRead
      ? ({ row }) => ({
          onClick: () => navigate(`/staff/view/${row.original.id}`),
          style: { cursor: "pointer" },
        })
      : undefined,
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  return (
    <div className="container-fluid center my-4">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Staffing
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          &nbsp;Staff
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex align-items-center justify-content-between p-1 mb-3"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="active dot"></div>
            </div>
            <span className="text-muted me-2">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Staff
              </span>
            </span>
          </div>
        </div>
        <div className="d-flex justify-content-between mb-3">
          <div className="d-lg-flex individual_fliters">
            <input type="hidden" name="centerId" value={filters.centerId} />

            <div className="form-group mb-0 mb-1 ms-2">
              <input
                type="text"
                name="teacherName"
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Staff Name"
                value={filters.teacherName}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <input
                type="text"
                name="country"
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Country"
                value={filters.country}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                type="text"
                className="form-select form-select-sm center_list"
                style={{ width: "160px" }}
                name="teacherType"
                value={filters.teacherType}
                onChange={handleFilterChange}
              >
                <option value="" selected>
                  Select Staff Type
                </option>
                <option value="Permanent">Permanent</option>
                <option value="Temporary">Temporary</option>
                <option value="Intern">Intern</option>
              </select>
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                type="text"
                className="form-select form-select-sm center_list"
                style={{ width: "160px" }}
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="" selected>
                  Select a status
                </option>
                <option value={"ACTIVE"}>Active</option>
                <option value={"RESIGNED"}>Resigned</option>
              </select>
            </div>

            <div className="form-group mb-0 mb-1 ms-2">
              <button
                type="button"
                className="btn btn-border btn-sm"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
          </div>
          {canCreate && (
            <Link to="/staff/add">
              <button
                type="button"
                className="btn btn-button btn-sm me-2"
                style={{ fontWeight: "600px !important" }}
              >
                &nbsp; Add &nbsp;&nbsp; <i className="bx bx-plus"></i>
              </button>
            </Link>
          )}
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {showActionColumn && (
              <Menu
                id="action-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {canUpdate && (
                  <MenuItem
                    onClick={() => navigate(`/staff/edit/${selectedId}`)}
                    className="text-start mb-0 menuitem-style"
                  >
                    Edit
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem>
                    <GlobalDelete
                      path={`/deleteUser/${selectedId}`}
                      onDeleteSuccess={fetchData}
                      onOpen={handleMenuClose}
                    />
                  </MenuItem>
                )}
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Staff;
